"use client";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Card, Grid, Link, Popover, Stack, Typography } from "@mui/material";
import type { DayOfWeek } from "@prisma/client";
import dayjs from "dayjs";
import {
    type ComponentProps,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";

import type { StoreData } from "@/server-fns/booking/query";
import { storeNow } from "@/utils/storeTime";

type Props = {
    data: StoreData;
    variant?: "full" | "banner" | "cards" | "stacked";
    wideImage?: boolean;
};

type OpeningHoursItem = {
    day: string;
    hours: string;
    isToday: boolean;
};

const DAY_ORDER: DayOfWeek[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const DAY_LABELS: Record<DayOfWeek, string> = {
    Monday: "Monday",
    Tuesday: "Tuesday",
    Wednesday: "Wednesday",
    Thursday: "Thursday",
    Friday: "Friday",
    Saturday: "Saturday",
    Sunday: "Sunday",
};

function formatTimeDisplay(hhmm: string): string {
    const [hours, minutes] = hhmm.split(":").map(Number);
    const period = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    return minutes === 0
        ? `${displayHours}${period}`
        : `${displayHours}:${minutes.toString().padStart(2, "0")}${period}`;
}

function getTodayIndex(tz: string): number {
    const today = storeNow(tz).day();
    return today === 0 ? 6 : today - 1;
}

function formatSpecialDayHours(specialDay: StoreData["specialDaySettings"][0]): string {
    if (!specialDay.isBookingEnabled) return "Closed";

    const enabledHours = specialDay.openingHours
        .filter((oh) => oh.isBookingEnabled)
        .sort((a, b) => a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM));

    if (enabledHours.length === 0) return "Regular hours";
    return enabledHours
        .map(
            (oh) =>
                `${formatTimeDisplay(oh.bookingStartTimeHHMM)} - ${formatTimeDisplay(oh.bookingEndTimeHHMM)}`,
        )
        .join(", ");
}

function formatSpecialDayDate(specialDay: StoreData["specialDaySettings"][0]): string {
    const start = specialDay.startDate.toISOString().slice(0, 10);
    const end = specialDay.endDate.toISOString().slice(0, 10);
    if (start === end) return dayjs(start).format("D MMM");
    return `${dayjs(start).format("D MMM")} – ${dayjs(end).format("D MMM")}`;
}

function getOpeningHoursFromData(data: StoreData, todayIndex: number): OpeningHoursItem[] {
    if (!data.dayOfWeekSettings || data.dayOfWeekSettings.length === 0) {
        return DAY_ORDER.map((day, index) => ({
            day: DAY_LABELS[day],
            hours: "Closed",
            isToday: index === todayIndex,
        }));
    }

    const settingsMap = new Map(
        data.dayOfWeekSettings.map((setting) => [setting.dayOfWeek, setting]),
    );

    return DAY_ORDER.map((day, index) => {
        const setting = settingsMap.get(day);
        const openingHours = setting?.openingHours || [];

        if (openingHours.length === 0) {
            return {
                day: DAY_LABELS[day],
                hours: "Closed",
                isToday: index === todayIndex,
            };
        }

        const sortedHours = [...openingHours]
            .filter((oh) => oh.isBookingEnabled)
            .sort((a, b) => a.bookingStartTimeHHMM.localeCompare(b.bookingStartTimeHHMM));

        if (sortedHours.length === 0) {
            return {
                day: DAY_LABELS[day],
                hours: "Closed",
                isToday: index === todayIndex,
            };
        }

        const hoursString = sortedHours
            .map(
                (oh) =>
                    `${formatTimeDisplay(oh.bookingStartTimeHHMM)} - ${formatTimeDisplay(
                        oh.bookingEndTimeHHMM,
                    )}`,
            )
            .join(", ");

        return {
            day: DAY_LABELS[day],
            hours: hoursString,
            isToday: index === todayIndex,
        };
    });
}

function getHoursSummary(data: StoreData): string {
    // todayIndex = -1: isToday is always false, which is fine since hoursSummary never uses it
    const openingHours = getOpeningHoursFromData(data, -1);
    const openDays = openingHours.filter((item) => item.hours !== "Closed");

    if (openDays.length === 0) {
        return "Closed";
    }

    const groups: { days: string[]; hours: string }[] = [];
    let currentGroup: { days: string[]; hours: string } | null = null;

    for (const item of openingHours) {
        if (item.hours === "Closed") {
            currentGroup = null;
            continue;
        }

        if (!currentGroup || currentGroup.hours !== item.hours) {
            currentGroup = { days: [item.day.slice(0, 3)], hours: item.hours };
            groups.push(currentGroup);
        } else {
            currentGroup.days.push(item.day.slice(0, 3));
        }
    }

    const formattedGroups = groups.map((group) => {
        if (group.days.length === 1) {
            return `${group.days[0]}: ${group.hours}`;
        } else if (group.days.length === 2) {
            return `${group.days[0]}, ${group.days[1]}: ${group.hours}`;
        } else {
            return `${group.days[0]}-${group.days[group.days.length - 1]}: ${group.hours}`;
        }
    });

    return formattedGroups.join("\n");
}

export const StoreInfo = ({ data, variant = "full", wideImage = false }: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    // -1 on SSR/initial render → no day is highlighted (avoids server/client timezone mismatch).
    // useEffect sets the real index after hydration on the client.
    const [todayIndex, setTodayIndex] = useState<number>(-1);

    useEffect(() => {
        setTodayIndex(getTodayIndex(data.timezone));
    }, [data.timezone]);

    const openingHours = useMemo(
        () => getOpeningHoursFromData(data, todayIndex),
        [data, todayIndex],
    );
    const hoursSummary = useMemo(() => getHoursSummary(data), [data]);
    const specialDays = useMemo(
        () =>
            (data.specialDaySettings ?? []).sort((a, b) =>
                a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0,
            ),
        [data],
    );

    const handleOpenHours = (event: { currentTarget: HTMLDivElement }) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseHours = () => {
        setAnchorEl(null);
    };

    const isHoursOpen = Boolean(anchorEl);

    const renderBanner = () => (
        <Stack spacing={2}>
            <Typography
                variant={variant === "banner" ? "h5" : "h3"}
                component="h1"
                sx={{
                    fontWeight: 800,
                    fontSize:
                        variant === "banner"
                            ? { xs: "1.25rem", md: "1.5rem" }
                            : { xs: "2rem", md: "2.75rem" },
                }}
            >
                {data.name}
            </Typography>
            <Card
                sx={{
                    height: wideImage ? { xs: 220, md: 400 } : { xs: 180, md: 300 },
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    width: "100%",
                }}
            >
                <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    <Box
                        component="img"
                        src={data.imageUrl ?? "/assets/images/restaurant-placeholder.svg"}
                        alt={data.name}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                    {!data.imageUrl && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.45)",
                                px: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                align="center"
                                sx={{ color: "white", fontWeight: 500 }}
                            >
                                This is a placeholder image. Upload a representative image in
                                Settings → Store Info.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Card>
        </Stack>
    );

    const renderCardList = (isStacked: boolean) => (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: isStacked ? 12 : 4 }}>
                <InfoCard
                    icon={<LocationOnIcon sx={{ color: "text.secondary", fontSize: 20 }} />}
                    title="Address"
                    content={`${data.streetAddress}, ${data.suburb} ${data.state} ${data.postcode}`}
                />
            </Grid>
            <Grid size={{ xs: 12, md: isStacked ? 12 : 4 }}>
                <InfoCard
                    icon={<PhoneIcon sx={{ color: "text.secondary", fontSize: 20 }} />}
                    title="Phone"
                    content={data.phone}
                />
            </Grid>
            <Grid size={{ xs: 12, md: isStacked ? 12 : 4 }}>
                <InfoCard
                    icon={<AccessTimeIcon sx={{ color: "text.secondary", fontSize: 20 }} />}
                    title="Hours"
                    content={hoursSummary}
                    onClick={handleOpenHours}
                    isClickable
                    contentSx={{ whiteSpace: "pre-line" }}
                    suffix={
                        <KeyboardArrowDownIcon
                            sx={{
                                fontSize: 18,
                                color: "text.disabled",
                                ml: "auto",
                                transform: isHoursOpen ? "rotate(180deg)" : "none",
                                transition: "0.2s",
                            }}
                        />
                    }
                />
            </Grid>
        </Grid>
    );

    const fullAddress = `${data.streetAddress}, ${data.suburb} ${data.state} ${data.postcode}`;

    // Stack resets child margins, so center with a flex wrapper rather than `mx: auto`.
    const renderRowList = () => (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "100%", maxWidth: 480 }}>
                <InfoRow
                    icon={<LocationOnIcon />}
                    content={fullAddress}
                    action={{
                        label: "Open in maps",
                        ariaLabel: "Open address in maps",
                        href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`,
                        external: true,
                    }}
                />
                <InfoRow
                    icon={<PhoneIcon />}
                    content={data.phone}
                    action={{
                        label: "Call",
                        ariaLabel: `Call ${data.phone}`,
                        href: `tel:${data.phone.replace(/[^\d+]/g, "")}`,
                    }}
                />
                <InfoRow
                    icon={<AccessTimeIcon />}
                    content={hoursSummary}
                    contentSx={{ whiteSpace: "pre-line" }}
                    onClick={handleOpenHours}
                    ariaLabel="Opening hours"
                    expanded={isHoursOpen}
                />
            </Box>
        </Box>
    );

    return (
        <Stack
            spacing={3}
            sx={{ width: "100%", mb: variant === "banner" || variant === "stacked" ? 0 : 6 }}
        >
            {(variant === "full" || variant === "banner") && renderBanner()}
            {variant === "full" && renderCardList(false)}
            {variant === "cards" && renderRowList()}
            {variant === "stacked" && renderCardList(true)}

            <Popover
                open={isHoursOpen}
                anchorEl={anchorEl}
                onClose={handleCloseHours}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            p: 2,
                            width: 280,
                            borderRadius: 3,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                            border: "1px solid rgba(0,0,0,0.05)",
                        },
                    },
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, px: 1 }}>
                    Opening Hours
                </Typography>
                <Stack spacing={1}>
                    {openingHours.map((item) => (
                        <Box
                            key={item.day}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1.5,
                                bgcolor: item.isToday ? "rgba(25, 118, 210, 0.05)" : "transparent",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: item.isToday ? 700 : 500,
                                    color: item.isToday ? "primary.main" : "text.primary",
                                }}
                            >
                                {item.day}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: item.isToday ? 700 : 400,
                                    color: item.isToday ? "primary.main" : "text.secondary",
                                }}
                            >
                                {item.hours}
                            </Typography>
                        </Box>
                    ))}
                </Stack>

                {specialDays.length > 0 && (
                    <>
                        <Box sx={{ my: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)" }} />
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, px: 1 }}>
                            Special Days
                        </Typography>
                        <Stack spacing={1}>
                            {specialDays.map((sd) => (
                                <Box
                                    key={sd.id}
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1.5,
                                        bgcolor: "rgba(0,0,0,0.02)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 600, color: "text.primary" }}
                                        >
                                            {sd.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: "text.secondary" }}
                                        >
                                            {formatSpecialDayDate(sd)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: sd.isBookingEnabled
                                                ? "text.secondary"
                                                : "error.main",
                                        }}
                                    >
                                        {formatSpecialDayHours(sd)}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </>
                )}
            </Popover>
        </Stack>
    );
};

const InfoCard = ({
    icon,
    title,
    content,
    onClick,
    isClickable,
    suffix,
    contentSx,
}: {
    icon: ReactNode;
    title: string;
    content: string;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    isClickable?: boolean;
    suffix?: ReactNode;
    contentSx?: ComponentProps<typeof Typography>["sx"];
}) => (
    <Box
        onClick={onClick}
        sx={{
            p: { xs: 2, md: 2.5 },
            height: "100%",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "rgba(0,0,0,0.06)",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            cursor: isClickable ? "pointer" : "default",
            transition: "0.2s",
            "&:hover": isClickable
                ? {
                      borderColor: "primary.main",
                      bgcolor: "rgba(25, 118, 210, 0.02)",
                  }
                : {},
        }}
    >
        <Box sx={{ mt: 0.3 }}>{icon}</Box>
        <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography
                variant="caption"
                component={"h1"}
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.7rem" }}
            >
                {title}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        lineHeight: 1.3,
                        fontSize: { xs: "0.8rem", md: "0.875rem" },
                        ...contentSx,
                    }}
                >
                    {content}
                </Typography>
                {suffix}
            </Box>
        </Stack>
    </Box>
);

type InfoRowAction = {
    label: string;
    ariaLabel: string;
    href: string;
    external?: boolean;
};

// Borderless row used by the "cards" variant: icon, text, then an optional right-aligned
// action link — or, for the hours row, a chevron that opens the weekly-hours popover.
const InfoRow = ({
    icon,
    content,
    contentSx,
    action,
    onClick,
    ariaLabel,
    expanded,
}: {
    icon: ReactNode;
    content: string;
    contentSx?: ComponentProps<typeof Typography>["sx"];
    action?: InfoRowAction;
    onClick?: (event: { currentTarget: HTMLDivElement }) => void;
    ariaLabel?: string;
    expanded?: boolean;
}) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick?.(event);
        }
    };

    return (
        <Box
            onClick={onClick}
            onKeyDown={onClick ? handleKeyDown : undefined}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? ariaLabel : undefined}
            aria-haspopup={onClick ? "dialog" : undefined}
            aria-expanded={onClick ? Boolean(expanded) : undefined}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.75,
                px: onClick ? 1 : 0,
                mx: onClick ? -1 : 0,
                borderRadius: 2,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                "&:last-of-type": { borderBottom: "none" },
                cursor: onClick ? "pointer" : "default",
                transition: "0.2s",
                "&:hover": onClick ? { bgcolor: "primary.lighter" } : {},
            }}
        >
            <Box sx={{ display: "flex", color: "primary.main", "& svg": { fontSize: 20 } }}>
                {icon}
            </Box>
            <Typography
                variant="body2"
                color="text.primary"
                sx={{
                    flex: 1,
                    lineHeight: 1.4,
                    fontSize: { xs: "0.8rem", md: "0.875rem" },
                    ...contentSx,
                }}
            >
                {content}
            </Typography>
            {action && (
                <Link
                    href={action.href}
                    aria-label={action.ariaLabel}
                    {...(action.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    underline="hover"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "primary.dark",
                    }}
                >
                    {action.label}
                    <NorthEastIcon sx={{ fontSize: 14 }} />
                </Link>
            )}
            {onClick && (
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        color: "text.disabled",
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "0.2s",
                    }}
                />
            )}
        </Box>
    );
};
